import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table'
import { useMemo, useState, useContext } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useMediaQuery, useTheme } from '@mui/material'
import CustomTable from '../../../../components/common/customTable'
import SiteIcon from '../../../../icons/siteIcon'
import RemoteIcon from '../../../../icons/remoteIcon'
import OpenCloseArrowIcon from '../../../../icons/openCloseArrowIcon'
import HelpIcon from '../../../../icons/helpIcon'
import CustomTooltip from '../../../../components/common/customToolptip'
import FormationTitleDialog from '../../../../components/common/formationTitleDialog'
import useOpenMoodleLink from '../../../../hooks/api/useOpenMoodleLink'
import { FormationType, FormationDetailsType, Schedule } from '../../../../types/api/formations'
import {
  useGetNextFormationQuery,
  useGetNextFormationDetailsQuery,
  useGetNextFormationOtherDatesQuery,
} from '../../../../hooks/api/formations'
import { getDate, getDayAndMonthNameYear, getFormatDate, shouldShowChangeDatesButton, formatTime, formatTimeFromAny, formatDateFromAny } from '../../../../utils'
import ContainedButton from '../../../../components/common/buttons/containedButton'
import Loader from '../../../../components/common/loader'
import CustomModal from '../../../../components/common/modals/customModal'
import { useFormik } from 'formik'
import Label from '../../../../components/common/label'
import RadioButton from '../../../../components/common/fields/radioButton'
import CustomizedAccordions from '../../../../components/common/accordion'

import FormikContext from '../../../../context/formikContext'
import RadioGroup from '@mui/material/RadioGroup'
import BodyModalUpdateDates from './bodyModalUpdateDates'
import BodyModalErreur from './bodyModaErreur'
import BodyModalSuccess from './bodyModalSuccess'

export const DateTitle = () => <Box sx={{ pl: '15px' }}>Date</Box>
export const DateBody = ({ row }: any) => (
  <Typography variant='smallTextNormal' pl='15px'>
    {getDate(row.original.start_date, row.original.end_date)}
  </Typography>
)
export const TypeBody = ({ row }: any) => (
  <Box sx={{ ml: '8px' }}>{row.original.type === 'EC' ? <SiteIcon /> : <RemoteIcon />}</Box>
)
export const TitleBody = ({ row }: any) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const theme = useTheme()
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down(1200))
  const openMoodleLink = useOpenMoodleLink()
  const handleMoodleLinkClick = (event: React.MouseEvent) => openMoodleLink(event, row.original.training_id)
  
  const handleClick = () => {
    setDialogOpen(true)
  }
  
  const InfoButton = () => (
    <Box
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        '&:hover svg path': {
          fill: '#5F686A',
        },
        flexShrink: 0,
        marginTop: '2px',
      }}
      component='span'
    >
      <HelpIcon />
    </Box>
  )
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
      {isMobileOrTablet ? (
        <CustomTooltip 
          title="Cliquez pour voir le titre complet"
          placement="top"
          arrow
        >
          <InfoButton />
        </CustomTooltip>
      ) : (
        <InfoButton />
      )}
      
      <Typography
        variant='h6'
        component='a'
        href={row.original.moodle_link}
        target='_blank'
        onClick={handleMoodleLinkClick}
        sx={{ 
          textDecoration: 'underline', 
          cursor: 'pointer',
          wordBreak: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          display: '-webkit-box',
          flex: 1
        }}
      >
        {row.original.title}
      </Typography>
      
      <FormationTitleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={row.original.title}
        moodleLink={row.original.moodle_link}
        onMoodleLinkClick={handleMoodleLinkClick}
      />
    </Box>
  )
}

const NextFormations = () => {
  const [page, setPage] = useState('1')
  const [openCangeDate, setOpenChangeDate] = useState(false)
  const [openSuccessModal, setOpenSuccessModal] = useState(false)
  const [dataId, setDatId] = useState<string>('')
  const [trainingId, setTrainingId] = useState<string>('')
  const [selectedFormation, setSelectedFormation] = useState<FormationType | null>(null)
  const [reqParam, setReqParam] = useState<object>({})
  const [value, setValue] = useState('')
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value)
  }

  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'start_date', desc: true }])
  const {
    data,
    isFetching,
    refetch: refreshData,
  } = useGetNextFormationQuery({
    page,
    sort: sorting[0],
  })
  const {
    data: dataDetails,
    status,
    isLoading: isLoadingDetails,
    refetch,
  } = useGetNextFormationDetailsQuery(dataId ?? '', { enabled: !!dataId })
  const { data: formationDates, status: formationDatesStatus, isLoading: isLoadingFormationDates, error: formationDatesError } = useGetNextFormationOtherDatesQuery(
    trainingId ?? '',
    { enabled: !!trainingId },
  )
  // Filtrer les formations par type et région ACQ (strict)
  const filteredFormationDates = useMemo(() => {
    if (!formationDates || !selectedFormation) return formationDates
    
    const filtered = formationDates.filter(formation => {
      // Filtrer par type (doit correspondre exactement)
      const sameType = formation.type === selectedFormation.type
      
      // Filtrer par ACQ régionale (comparer les codes)
      const sameRegion = formation.acq_region?.code === selectedFormation.acq_region?.code
      
      return sameType && sameRegion
    })
        
    return filtered
  }, [formationDates, selectedFormation])
  
  const handleOpenModal = () => {
    setTrainingId(dataId)
    setOpenChangeDate(true)
  }

  const handleCloseModal = () => {
    setOpenChangeDate(false)
    setSelectedFormation(null)
    // setSelectedModal(null)
  }
  
  const handleSuccess = () => {
    setOpenChangeDate(false)
    setOpenSuccessModal(true)
  }

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false)
  }

  const columns = useMemo<MRT_ColumnDef<FormationType>[]>(
    () => [
      {
        header: 'Date',
        accessorKey: 'start_date',
        Header: DateTitle,
        disablePadding: false,
        enableSorting: true,
        Cell: DateBody,
        size: 36,
        minSize: 36,
        maxSize: 36,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        disablePadding: false,
        enableSorting: true,
        Cell: TypeBody,
        size: 10,
        minSize: 10,
        maxSize: 10,
      },
      {
        accessorKey: 'title',
        header: 'Formation',
        enableSorting: true,
        disablePadding: false,
        Cell: TitleBody,
        size: 55,
        minSize: 55,
        maxSize: 55,
      },
    ],
    [],
  )
  const ExpandBody = ({ row, table }: any) => (
    <Box
      onClick={() => {
        if (!row.getIsExpanded()) {
          setDatId(row.original.training_id)
          table.resetExpanded()
        }
        row.toggleExpanded()
      }}
      sx={{
        mr: '12px',
        cursor: 'pointer',
        p: '5px',
        display: 'flex',
        gap: '9px',
      }}
    >
      <Typography variant='smallTextNormal'>Voir l’horaire</Typography>
      <OpenCloseArrowIcon open={row.getIsExpanded()} />
    </Box>
  )
  const DetailPanel = ({ row }: any) => (
    <Box sx={{ display: 'flex', gap: '8%', alignItems: 'flex-start', width: '100%' }}>
      {isLoadingDetails ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            mt: '10px',
          }}
        >
          <Loader />
        </Box>
      ) : (
        <>
          <Box width='20%'>
            {(() => {
              if (isLoadingDetails) return null
              const firstSchedule = dataDetails?.schedule?.[0]
              const startTime = firstSchedule?.start_time || '00:00'
              
              return shouldShowChangeDatesButton(
                row.original.start_date, 
                startTime, 
                row.original.category,
              ) ? (
                <ContainedButton
                  sx={{
                    alignSelf: 'start',
                    width: '180px',
                    py: '12px',
                    fontSize: '12px',
                    lineHeight: '16px',
                    minHeight: 'auto',
                  }}
                  variant='principal'
                  title='Changer de dates'
                  handleClick={() => {
                    // Enrichir la formation avec les données de dataDetails
                    const enrichedFormation = {
                      ...row.original,
                      type: dataDetails?.type || row.original.type,
                      acq_region: dataDetails?.acq_region || row.original.acq_region
                    }
                    setSelectedFormation(enrichedFormation)
                    handleOpenModal()
                  }}
                />
              ) : null
            })()}
          </Box>
          
          {dataDetails?.schedule && dataDetails?.schedule?.length > 0 ? (
            <Box>
              <Typography variant='h5' mb='10px' sx={{ letterSpacing: 'inherit' }}>
                Horaire
              </Typography>
              <Typography
                component='ul'
                style={{ listStyle: 'none', paddingInlineStart: 0 }}
                pl='26px'
                variant='smallTextNormal'
              >
                {dataDetails?.schedule?.map((item: null | Schedule, index: number) => (
                  <Typography component='li' key={`${item?.date}_${item?.start_time}_${index}`}>
                    {formatDateFromAny(item?.date)}, {formatTimeFromAny(item?.start_time)} à {formatTimeFromAny(item?.end_time)}
                  </Typography>
                ))}
              </Typography>
            </Box>
          ) : null}

          {row.original.type === 'EC' ? (
            <Box width='20%'>
              <Typography variant='h5' mb='10px' sx={{ letterSpacing: 'inherit' }}>
                Adresse
              </Typography>
              <Typography variant='smallTextNormal'>
                {[dataDetails?.address, dataDetails?.city, dataDetails?.postal_code].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  )

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      className='detail_pannel nextFormations'
    >
      <CustomTable
        muiTableBodyRowProps={({ row, isDetailPanel }) => ({
          sx: {
            ...(isDetailPanel &&
              !row.getIsExpanded() && {
                height: '0px',
                overflow: 'hidden',
              }),
          },
          hover: false,
        })}
        data={data?.data ?? []}
        columns={columns}
        enableSortingRemoval={false}
        enableSorting={true}
        enableMultiSort={false}
        manualSorting
        onSortingChange={(params) => {
          setPage('1')
          setSorting(params)
        }}
        state={{ sorting, isLoading: isFetching }}
        enableExpandAll={false}
        enableExpanding
        autoResetExpanded
        renderDetailPanel={DetailPanel}
        displayColumnDefOptions={{
          'mrt-row-expand': {
            header: '',
            size: 15,
            minSize: 15,
            maxSize: 15,
            muiTableBodyCellProps: ({ row }) => ({
              sx: {
                px: '0',
                justifyContent: 'end',
                borderBottom: '1px solid #DADCDD',
                ...(row.getIsExpanded() ? { borderBottom: 'none' } : {}),
              },
            }),
            Cell: ExpandBody,
          },
        }}
        muiTableDetailPanelProps={({ row, table }) => ({
          sx: {
            width: '100%',
            pt: '3px',
            display: 'table-cell',
            ...(row.getIsExpanded() ? { pb: '36px' } : {}),
          },
        })}
        propsPagination={{
          page: Number(page),
          count:
            data?.metadata?.total && Number(data.metadata.total) > 0
              ? Math.ceil(Number(data.metadata.total) / 10)
              : 1,
          onChange: (e, index) => {
            setPage(index.toString())
          },
        }}
      />
      <CustomModal
        open={openCangeDate}
        handleClose={handleCloseModal}
        sx={{
          width: {
            xs: '95vw',
            sm: '90vw',
            md: '1000px',
          },
        }}
      >
        {isLoadingFormationDates ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              mt: '10px',
            }}
          >
            <Loader />
          </Box>
        ) : filteredFormationDates && filteredFormationDates?.length > 0 ? (
          <BodyModalUpdateDates
            dataDates={filteredFormationDates}
            status={formationDatesStatus}
            handleClose={handleCloseModal}
            oldTrainingId={trainingId}
            isRefreshed={refetch}
            isRefreshedData={refreshData}
            onSuccess={handleSuccess}
          ></BodyModalUpdateDates>
        ) : (
          <BodyModalErreur
            handleClose={handleCloseModal}
            sx={{
              alignItems: 'center',
              '& .modal-header': {
                flexDirection: 'column',
              },
            }}
          />
        )}
      </CustomModal>
       <CustomModal
        open={openSuccessModal}
        handleClose={handleCloseSuccessModal}
        sx={{
          width: {
            xs: '95vw',
            sm: '90vw',
            md: '600px',
          },
        }}
      >
        <BodyModalSuccess
          handleClose={handleCloseSuccessModal}
          sx={{
            alignItems: 'center',
            '& .modal-header': {
              flexDirection: 'column',
            },
          }}
        />
      </CustomModal>
    </Box>
  )
}

export default NextFormations
